import { Check } from 'lucide-react';
import { useEventTypes } from '../../../hooks/useEvents';
import { EventTypeSelect } from '../../common/EventTypeSelect';

/** More types than this and the cards give way to the searchable picker. */
export const MAX_TYPE_CARDS = 9;

export interface EventTypeCardsProps {
  value: string | null;
  onChange: (eventTypeId: string) => void;
}

/**
 * The event types from Settings as a grid of cards, one tap each. An office
 * with a long list of types gets the searchable picker instead, since a wall
 * of cards is slower to read than a search box.
 */
export function EventTypeCards({ value, onChange }: EventTypeCardsProps) {
  const { data: all = [], isLoading } = useEventTypes();
  const types = all
    .filter((t) => t.isActive !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 animate-pulse" aria-busy>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 rounded-xl bg-gray-100" />
        ))}
      </div>
    );
  }

  if (types.length > MAX_TYPE_CARDS) {
    return (
      <EventTypeSelect
        value={value}
        onChange={(id) => id && onChange(id)}
        placeholder="Select event type"
        ariaLabel="Event Type"
      />
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Event Type"
      className="grid grid-cols-2 sm:grid-cols-3 gap-2"
    >
      {types.map((t) => {
        const selected = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(t.id)}
            className={`relative min-h-12 px-3 py-2.5 rounded-xl border text-sm font-medium text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
              selected
                ? 'border-primary-500 bg-primary-50 text-primary-800'
                : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
            }`}
          >
            {t.name}
            {selected && (
              <Check size={14} className="absolute top-2 right-2 text-primary-600" aria-hidden />
            )}
          </button>
        );
      })}
    </div>
  );
}
